// /quotes: every quote the caller may see, with plain statuses and the mock's filters, and
// above them the quote documents still waiting on someone (M7).
// /quotes/templates: the dealer's quote templates (M3), and for Phoneware every dealer's.
// Sales at Phoneware quotes the house dealer's customers and reads the rest; Billing ops
// and Support read every dealer's quotes and change none (mock M1).
import React, { useEffect, useState } from "react";
import {
  formatCents,
  quoteTotals,
  roleLabel,
  type Dealer,
  type LensUser,
  type Quote,
  type QuoteIntakeSummary,
  type QuoteStatus,
  type QuoteTemplateDetail,
  type QuoteType,
} from "@phoneware/lens-domain";
import { Icon } from "../icons.js";
import { Link, navigate, rowLink } from "../router.js";
import { useLoad, useSession } from "../session.js";
import { QUOTE_STATUS, QUOTE_TYPE, plural, when } from "../format.js";
import { Callout, Chip, Head, QuoteStatusChip, ViewOnly, useTitle } from "../ui.js";
import { NewQuoteMenu, OpsNewQuoteSheet, TemplatesCard } from "./Templates.js";
import { MadeBy, madeByApp } from "./connect-bits.js";
import { intakeNumberOf, isOpenIntake } from "../api-intakes.js";
import { QuoteDocumentsCard, UploadSheet } from "./intake-bits.js";

/** How often Quotes asks again while a quote document is still being read. */
const READING_POLL_MS = 4000;

/** Names for the reps behind quotes: owners and Phoneware can list people; everyone else knows only themselves. */
export function useRepNames(): (email: string) => string {
  const { me, api, can, everyDealer } = useSession();
  const canList = everyDealer || can("users.manage");
  const users = useLoad<LensUser[]>(() => (canList ? api.users() : Promise.resolve([])), [canList]);
  return (email) => (email === me.user.email ? me.user.name : users.data?.find((u) => u.email === email)?.name ?? email);
}

export function Quotes({ tab }: { tab: "quotes" | "templates" }): React.ReactElement {
  const { me, api, can, everyDealer, writeScope, canWrite, seesPrices } = useSession();
  const ops = everyDealer;
  // Tech sees no prices, so no quotes and no templates. Whoever holds quotes.write starts quotes and keeps templates.
  const canQuote = seesPrices;
  const writes = can("quotes.write");
  const quoteScope = writeScope("quotes.write");
  const showTemplates = canQuote && tab === "templates";
  useTitle(showTemplates ? "Templates" : "Quotes");
  const quotes = useLoad<Quote[]>(() => api.quotes(), []);
  const dealers = useLoad<Dealer[]>(() => (ops ? api.dealers() : Promise.resolve([])), [ops]);
  const templates = useLoad<QuoteTemplateDetail[]>(() => (canQuote ? api.quoteTemplates() : Promise.resolve([])), [canQuote]);
  const repName = useRepNames();
  const [status, setStatus] = useState<QuoteStatus | "">("");
  const [who, setWho] = useState("");
  const [type, setType] = useState<QuoteType | "">("");
  const [find, setFind] = useState("");
  // Ops start a quote from the customer, so New quote and Start a quote open a sheet rather than the builder.
  const [opsNew, setOpsNew] = useState<{ from: QuoteTemplateDetail | null } | null>(null);
  // Quote documents (M7): the ones not confirmed or discarded, and New quote's From a quote document.
  const intakes = useLoad<QuoteIntakeSummary[]>(() => (writes ? api.quoteIntakes() : Promise.resolve([])), [writes]);
  const openDocs = (intakes.data ?? []).filter(isOpenIntake);
  const reading = openDocs.some((ik) => ik.state === "extracting");
  const [upload, setUpload] = useState(false);
  useEffect(() => {
    if (!reading) return;
    const timer = window.setTimeout(() => intakes.reload(), READING_POLL_MS);
    return () => window.clearTimeout(timer);
    // `intakes` is a fresh object every render; a new answer is what re-arms the timer.
  }, [intakes.data, reading]);

  const all = quotes.data ?? [];
  const tpls = templates.data ?? [];
  const whoOptions = ops
    ? (dealers.data ?? []).map((d) => [d.id, d.name] as const)
    : [...new Set(all.map((q) => q.repEmail))].map((email) => [email, repName(email)] as const);
  const needle = find.trim().toLowerCase();
  const list = all
    .filter(
      (q) =>
        (!status || q.status === status) &&
        (!type || q.type === type) &&
        (!who || (ops ? q.dealerId : q.repEmail) === who) &&
        (!needle || `${q.number} ${q.customerName}`.toLowerCase().includes(needle)),
    )
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const acting = all.filter((q) => q.status === "draft" || q.status === "needs_approval" || q.status === "viewed").length;
  const dealerName = me.dealer?.name ?? "";

  const sub = showTemplates
    ? !templates.data
      ? "Loading templates."
      : ops
        ? `${plural(tpls.length, "template")} across ${plural(new Set(tpls.map((t) => t.dealerId)).size, "dealer")}. Each starts a quote at today\u2019s prices.`
        : tpls.length
          ? `${plural(tpls.length, "template")} for ${dealerName}. Each starts a quote at today\u2019s prices.`
          : `No templates for ${dealerName} yet.`
    : quotes.data
      ? `${plural(all.length, "quote")}${ops ? " across every dealer." : `. ${acting} still need someone to act.`}${
          all.some((q) => madeByApp(q.createdVia)) ? " A draft an AI or app made names it and the person it worked for." : ""
        }`
      : "Loading quotes.";

  const start = (t: QuoteTemplateDetail) => (ops ? setOpsNew({ from: t }) : navigate(`/quotes/new?from=${encodeURIComponent(t.id)}`));

  const actions = !writes ? null : ops ? (
    <>
      <button className="btn" type="button" aria-haspopup="dialog" data-testid="quotes-from-document" onClick={() => setUpload(true)}>
        <Icon name="file-up" size={15} />
        From a quote document
      </button>
      <button className="btn primary" type="button" aria-haspopup="dialog" data-testid="quotes-new" onClick={() => setOpsNew({ from: null })}>
        <Icon name="plus" size={15} />
        New quote
      </button>
    </>
  ) : (
    <NewQuoteMenu templates={templates.data} onFromDocument={() => setUpload(true)} />
  );

  return (
    <>
      <Head title="Quotes" sub={sub} actions={actions} />
      {canQuote ? (
        <nav className="tabs" aria-label="Quotes and templates">
          <Link to="/quotes" aria-current={showTemplates ? undefined : "page"} data-testid="tab-quotes">
            Quotes {quotes.data ? <span className="n">{all.length}</span> : null}
          </Link>
          <Link to="/quotes/templates" aria-current={showTemplates ? "page" : undefined} data-testid="tab-templates">
            Templates {templates.data ? <span className="n">{tpls.length}</span> : null}
          </Link>
        </nav>
      ) : null}
      {ops && quoteScope === "house" ? (
        <Callout icon="info">
          <span data-testid="quotes-house-note">You quote for Phoneware&rsquo;s own customers. Other dealers&rsquo; quotes are view only.</span>
        </Callout>
      ) : ops && !writes ? (
        <ViewOnly testId="quotes-view-only">{roleLabel(me.user.role)} sees every dealer&rsquo;s quotes and changes none of them.</ViewOnly>
      ) : null}
      {!showTemplates && openDocs.length ? <QuoteDocumentsCard intakes={openDocs} name={repName} dealers={ops ? (dealers.data ?? []) : null} /> : null}
      {showTemplates ? (
        templates.error ? (
          <Callout kind="stop" icon="triangle-alert">
            {templates.error.message}
          </Callout>
        ) : !templates.data ? (
          <section className="card">
            <div className="empty">Loading templates.</div>
          </section>
        ) : (
          <TemplatesCard
            ops={ops}
            templates={tpls}
            dealers={dealers.data ?? []}
            repName={repName}
            firstDraft={all.filter((q) => q.status === "draft" && q.repEmail === me.user.email).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0] ?? null}
            onReload={templates.reload}
            onStart={start}
          />
        )
      ) : (
        <section className="card">
          <div className="filters">
            <label className="search grow">
              <Icon name="search" size={15} />
              <span className="sr">Find a quote</span>
              <input className="input" type="search" placeholder="Customer or quote number" value={find} onChange={(e) => setFind(e.target.value)} />
            </label>
            <label className="field">
              <span className="sr">Status</span>
              <select className="input" aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as QuoteStatus | "")}>
                <option value="">All statuses</option>
                {(Object.keys(QUOTE_STATUS) as QuoteStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {QUOTE_STATUS[s].label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="sr">{ops ? "Dealer" : "Rep"}</span>
              <select className="input" aria-label={ops ? "Dealer" : "Rep"} value={who} onChange={(e) => setWho(e.target.value)}>
                <option value="">{ops ? "All dealers" : "Every rep"}</option>
                {whoOptions.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <div className="seg" role="group" aria-label="Type">
              {(["", "new", "change", "renewal"] as Array<QuoteType | "">).map((t) => (
                <button key={t || "all"} type="button" aria-pressed={type === t} onClick={() => setType(t)}>
                  {t ? QUOTE_TYPE[t] : "All"}
                </button>
              ))}
            </div>
          </div>
          {quotes.error ? (
            <Callout kind="stop" icon="triangle-alert">
              {quotes.error.message}
            </Callout>
          ) : !quotes.data ? (
            <div className="empty">Loading quotes.</div>
          ) : all.length === 0 ? (
            <div className="empty">{writes ? "No quotes yet. Start one with New quote." : "No quotes yet."}</div>
          ) : list.length === 0 ? (
            <div className="empty">No quotes match these filters.</div>
          ) : (
            <div className="tablewrap">
              <table className="t t-quotes">
                <thead>
                  <tr>
                    <th>Quote</th>
                    <th>Customer</th>
                    <th className="hide-sm">Type</th>
                    <th className="hide-sm">{ops ? "Dealer" : "Rep"}</th>
                    <th>Status</th>
                    <th className="num">Monthly</th>
                    <th className="num hide-sm">One-time</th>
                    <th className="hide-sm">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((q) => {
                    const dealer = dealers.data?.find((d) => d.id === q.dealerId) ?? null;
                    const t = quoteTotals(q.lines, dealer?.model ?? me.dealer?.model ?? "agent");
                    return (
                      <tr key={q.id} {...rowLink(`/quotes/${encodeURIComponent(q.id)}`)}>
                        <td className="mono">
                          <Link to={`/quotes/${encodeURIComponent(q.id)}`}>{q.number}</Link>
                        </td>
                        <td>
                          <b>{q.customerName}</b>
                          {ops && writes && !canWrite("quotes.write", dealer) ? (
                            <span className="sub">
                              <Chip kind="quiet" icon="eye" testId={`quote-view-only-${q.id}`}>
                                View only
                              </Chip>
                            </span>
                          ) : null}
                          <MadeBy via={q.createdVia} person={repName(q.repEmail)} block testId={`quote-made-by-${q.id}`} />
                          {intakeNumberOf(q) ? <span className="from">From a quote document</span> : null}
                          {/* Plan 7C: a MAC quote names the support ticket it came from. */}
                          {q.type === "change" && q.source ? <span className="sub" data-testid={`quote-source-${q.id}`}>From ticket {q.source.ticketNumber}</span> : null}
                        </td>
                        <td className="hide-sm">{QUOTE_TYPE[q.type]}</td>
                        <td className="hide-sm">{ops ? dealer?.name ?? "" : repName(q.repEmail)}</td>
                        <td>
                          <QuoteStatusChip status={q.status} />
                        </td>
                        <td className="num">{formatCents(t.monthly)}</td>
                        <td className="num hide-sm">{formatCents(t.oneTime)}</td>
                        <td className="muted hide-sm">{when(q.updatedAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
      {opsNew ? <OpsNewQuoteSheet from={opsNew.from} templates={tpls} dealers={dealers.data ?? []} onClose={() => setOpsNew(null)} /> : null}
      {upload ? <UploadSheet onClose={() => setUpload(false)} /> : null}
    </>
  );
}
